/**
 * ScoreSystem — pontuação, combo, multiplicador e bônus de velocidade.
 *
 * Regras (CLAUDE.md §4.4):
 *
 * - Base: 10 pontos por acerto.
 * - Multiplicador cresce +0.5 a cada 5 acertos consecutivos, com teto em x3.
 *   (combo 0–4 = 1.0; 5–9 = 1.5; 10–14 = 2.0; 15–19 = 2.5; 20+ = 3.0)
 * - Bônus de velocidade: se a resposta veio em < 50% do tempo limite, soma
 *   SPEED_BONUS_BASE (também afetado pelo multiplicador). Mantém o ritmo
 *   "no compasso da música" — premia fluência sem punir lentidão.
 * - Erro zera o combo, mas nunca diminui pontos já ganhos.
 *
 * Stats acumulados (corretas, vidas perdidas, bônus de velocidade) servem
 * para calcular estrelas na ResultScene — por isso ficam neste sistema e
 * não na cena.
 *
 * Sistema puro: testável sem Phaser.
 */

const POINTS_BASE = 10;
const SPEED_BONUS_BASE = 5;
const COMBO_STEP = 5;
const COMBO_INCREMENT = 0.5;
const MAX_MULTIPLIER = 3.0;
const SPEED_THRESHOLD = 0.5;

export interface CorrectResult {
  /** Pontos ganhos nesta resposta (já aplicado multiplicador e bônus). */
  points: number;
  /** Combo após este acerto (1, 2, 3...). */
  combo: number;
  /** Multiplicador aplicado nesta resposta. */
  multiplier: number;
  /** Verdadeiro se ganhou bônus de velocidade. */
  speedBonus: boolean;
}

export interface ScoreSnapshot {
  score: number;
  combo: number;
  multiplier: number;
  correctCount: number;
  wrongCount: number;
  speedBonusCount: number;
  /** Razão de respostas corretas que ganharam bônus de velocidade. */
  speedBonusRate: number;
}

export class ScoreSystem {
  private score = 0;
  private combo = 0;
  private correctCount = 0;
  private wrongCount = 0;
  private speedBonusCount = 0;

  recordCorrect(responseTimeMs: number, timeLimitMs: number): CorrectResult {
    this.combo += 1;
    this.correctCount += 1;

    const multiplier = this.computeMultiplier(this.combo);
    const speedBonus = timeLimitMs > 0 && responseTimeMs <= timeLimitMs * SPEED_THRESHOLD;

    const baseGain = POINTS_BASE + (speedBonus ? SPEED_BONUS_BASE : 0);
    const points = Math.round(baseGain * multiplier);

    this.score += points;
    if (speedBonus) this.speedBonusCount += 1;

    return { points, combo: this.combo, multiplier, speedBonus };
  }

  recordWrong(): void {
    this.combo = 0;
    this.wrongCount += 1;
  }

  reset(): void {
    this.score = 0;
    this.combo = 0;
    this.correctCount = 0;
    this.wrongCount = 0;
    this.speedBonusCount = 0;
  }

  snapshot(): ScoreSnapshot {
    return {
      score: this.score,
      combo: this.combo,
      multiplier: this.computeMultiplier(this.combo),
      correctCount: this.correctCount,
      wrongCount: this.wrongCount,
      speedBonusCount: this.speedBonusCount,
      speedBonusRate: this.correctCount === 0 ? 0 : this.speedBonusCount / this.correctCount,
    };
  }

  getScore(): number {
    return this.score;
  }

  getCombo(): number {
    return this.combo;
  }

  getMultiplier(): number {
    return this.computeMultiplier(this.combo);
  }

  private computeMultiplier(combo: number): number {
    if (combo < COMBO_STEP) return 1.0;
    const tier = Math.floor(combo / COMBO_STEP);
    return Math.min(1.0 + tier * COMBO_INCREMENT, MAX_MULTIPLIER);
  }
}

/**
 * Cálculo de estrelas — pedagogia (CLAUDE.md §7) pede tom de incentivo,
 * não punitivo. Com 3 vidas iniciais, a régua é:
 *
 * - 0★ Game over (acabaram as vidas — nem completou).
 * - 1★ Completou perdendo 2 vidas (passou raspando).
 * - 2★ Completou perdendo no máximo 1 vida.
 * - 3★ Completou sem perder vida E ≥ 75% das corretas com bônus de
 *      velocidade.
 *
 * Régua anterior dava 1★ pra qualquer vida perdida — punia demais quem
 * errou só uma de muitas. A nova distribui melhor entre os três tiers.
 */
export interface StarsInput {
  completed: boolean;
  livesLost: number;
  speedBonusRate: number;
  correctCount: number;
}

const SPEED_STAR_THRESHOLD = 0.75;

export function calculateStars(input: StarsInput): 0 | 1 | 2 | 3 {
  if (!input.completed) return 0;
  if (input.livesLost >= 2) return 1;
  if (input.livesLost === 0 && input.correctCount > 0 && input.speedBonusRate >= SPEED_STAR_THRESHOLD) return 3;
  return 2;
}
